import React from 'react';
import { AlertCircle, RefreshCw, X } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ChatError } from '../../services/ai/fastApiBackend';

interface ChatErrorBannerProps {
  error: ChatError | null;
  /** Disables Retry while a (retry) request is in flight. */
  isStreaming: boolean;
  onRetry: () => void;
  onDismiss: () => void;
}

/**
 * Inline, dismissible error banner shown above the chat input. Renders a distinct,
 * actionable message per failure mode and offers Retry for retryable errors.
 */
export const ChatErrorBanner: React.FC<ChatErrorBannerProps> = ({
  error,
  isStreaming,
  onRetry,
  onDismiss,
}) => {
  if (!error) {
    return null;
  }

  return (
    <div className="px-3 pb-2 flex-shrink-0">
      <Alert variant="destructive" className="relative pr-8">
        <AlertCircle className="h-4 w-4" />
        <button
          type="button"
          aria-label="Dismiss error"
          onClick={onDismiss}
          className="absolute right-2 top-2 text-destructive/70 transition-colors hover:text-destructive"
        >
          <X className="h-4 w-4" />
        </button>
        <AlertTitle>{error.title}</AlertTitle>
        <AlertDescription>
          <p>{error.message}</p>
          {error.retryable && (
            <Button
              variant="outline"
              size="sm"
              className="mt-2 h-7"
              onClick={onRetry}
              disabled={isStreaming}
            >
              <RefreshCw className="mr-1 h-3 w-3" />
              Retry
            </Button>
          )}
        </AlertDescription>
      </Alert>
    </div>
  );
};
